/**
 * 서버 시각을 **운영자가 고른 타임존**으로 그린다.
 *
 * 서버는 시각을 UTC 로 저장하고 ISO 문자열로 내려준다(`+00:00`). 예전 백테스트 화면은 그
 * 문자열을 `slice(0, 19)` 로 잘라서 그대로 찍었다 — 그래서 KST 22:45 에 돌린 백테스트가
 * "13:45" 로 보였고, 같은 규칙의 주문 화면(`toLocaleString`)과 9시간 어긋났다.
 *
 * 타임존은 `me.timezone`(기본 `Asia/Seoul`)이고 **표시 전용**이다. 진입 창·하루 버킷은 서버가
 * KST 로 고정해 판정하므로 이 값을 바꿔도 규칙이 도는 시각은 그대로다.
 */

export const DEFAULT_TIME_ZONE = "Asia/Seoul";

/**
 * 설정 화면이 내놓는 선택지. 브라우저의 전체 목록(`Intl.supportedValuesOf`, 400여 개)을 그대로
 * 늘어놓으면 고를 수가 없어서, 이 앱을 쓰는 자리(국내·미국장)에 맞춘 몇 개만 둔다. 서버는 IANA
 * 전체를 받으므로 MCP 로 다른 값을 넣으면 화면이 그 값을 선택지에 더해 보여준다.
 */
export const TIME_ZONE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "Asia/Seoul", label: "서울 (Asia/Seoul)" },
  { value: "UTC", label: "협정 세계시 (UTC)" },
  { value: "America/New_York", label: "뉴욕 (America/New_York)" },
  { value: "America/Chicago", label: "시카고 (America/Chicago)" },
  { value: "America/Los_Angeles", label: "로스앤젤레스 (America/Los_Angeles)" },
  { value: "Europe/London", label: "런던 (Europe/London)" },
  { value: "Europe/Berlin", label: "베를린 (Europe/Berlin)" },
  { value: "Asia/Tokyo", label: "도쿄 (Asia/Tokyo)" },
  { value: "Asia/Hong_Kong", label: "홍콩 (Asia/Hong_Kong)" },
  { value: "Asia/Singapore", label: "싱가포르 (Asia/Singapore)" },
  { value: "Australia/Sydney", label: "시드니 (Australia/Sydney)" },
];

/** 이 브라우저의 `Intl` 이 아는 타임존인가. 모르는 값으로 포맷하면 `RangeError` 로 화면이 죽는다. */
export function isValidTimeZone(timeZone: string): boolean {
  if (timeZone === "") return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone });
    return true;
  } catch {
    return false;
  }
}

/** 설정값을 쓸 수 있으면 그대로, 없거나 브라우저가 모르면 기본값(서울). */
export function resolveTimeZone(timeZone: string | null | undefined): string {
  return typeof timeZone === "string" && isValidTimeZone(timeZone) ? timeZone : DEFAULT_TIME_ZONE;
}

/**
 * 서버 ISO 문자열 → `Date`. 오프셋이 없는(naive) 문자열은 **UTC 로** 읽는다 — 서버는 UTC 로만
 * 저장하는데, `new Date("2026-11-03T13:45:12")` 는 브라우저 로컬 시각으로 해석해서 그만큼 틀린다.
 */
export function parseServerInstant(iso: string | null | undefined): Date | null {
  if (typeof iso !== "string" || iso.trim() === "") return null;
  const trimmed = iso.trim();
  const hasTime = trimmed.includes("T");
  const hasOffset = /(Z|[+-]\d{2}:?\d{2})$/i.test(trimmed);
  const date = new Date(hasTime && !hasOffset ? `${trimmed}Z` : trimmed);
  return Number.isNaN(date.getTime()) ? null : date;
}

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatterFor(timeZone: string): Intl.DateTimeFormat {
  let formatter = formatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      // `hour12: false` 는 엔진에 따라 자정을 "24" 로 찍는다. h23 이 00~23 을 보장한다.
      hourCycle: "h23",
    });
    formatters.set(timeZone, formatter);
  }
  return formatter;
}

/** `GMT+9`·`GMT-5`·`GMT` — 같은 타임존도 서머타임으로 오프셋이 바뀌므로 그 시각 기준이다. */
export function timeZoneOffsetLabel(timeZone: string, at: Date): string {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      timeZoneName: "shortOffset",
    }).formatToParts(at);
    return parts.find((part) => part.type === "timeZoneName")?.value ?? "";
  } catch {
    return "";
  }
}

/**
 * `2026-11-03 22:45:12` 형태(초까지). 같은 날 여러 번 돌린 백테스트를 초 단위로 가르던 기존
 * 표기를 그대로 두고 타임존만 바꾼다. `withZone` 이면 뒤에 `GMT+9` 를 붙인다 — 설정을 바꾼
 * 사람이 아니면 이 시각이 어느 기준인지 화면만 보고 알 수 없다.
 */
export function formatDateTime(
  iso: string | null | undefined,
  timeZone: string,
  options: { withZone?: boolean } = {}
): string {
  const date = parseServerInstant(iso);
  if (!date) return "—";
  const zone = resolveTimeZone(timeZone);
  const parts = Object.fromEntries(
    formatterFor(zone)
      .formatToParts(date)
      .map((part) => [part.type, part.value])
  );
  const text = `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`;
  if (!options.withZone) return text;
  const label = timeZoneOffsetLabel(zone, date);
  return label ? `${text} ${label}` : text;
}
